/**
 * src/components/pipeline/quote/copilot/useQuoteCopilotChat.js — TICKET-912 slice 1.
 * Design: .planning/plans/2026-09-24-quote-copilot-v2/DESIGN.md
 *
 * The Quote Copilot's state, exposed with the SAME interface as useChat
 * (messages, input, setInput, send, stop, loading, hydrating, error, scope, title,
 * sessionId) so the floating bot's AgentShell renders it with no second chat UI.
 * Plus the copilot extras: items, revert, reverted, revertingId, copyForEmail.
 *
 * One thread per DEAL (quote_copilot_turns). A turn and a revert go through
 * agent.chat scope 'quote_copilot' (src/lib/api/quoteCopilot.js). After either,
 * the thread and the open items are re-read, so the screen shows what the server holds.
 *
 * onBeforeTurn — async; the quote builder flushes its pending edits BEFORE the paid
 *                turn reads and rewrites the quote. {ok:false} stops the turn (TICKET-899).
 * onChanged    — the turn/revert wrote to the quote; the host reloads it.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useT } from '../../../../lib/i18n/I18nContext';
import { useToast } from '../../../shared/ToastProvider';
import {
  fetchCopilotTurns, fetchCopilotOpenItems, streamCopilotTurn, revertCopilotTurn,
} from '../../../../lib/api/quoteCopilot';
import { turnsToMessages } from './copilotChanges';

const noop = () => {};

/**
 * Pure: live progress events -> the lines shown in the pending answer bubble
 * (TICKET-912 slice 2b). One line per tool, humanised; a trailing "Thinking…" while the
 * model is working after the last tool.
 */
export function progressLines(events, t) {
  const lines = [];
  for (const e of events) {
    if (e?.type !== 'tool') continue;
    const label = t(`quoteCopilot.progress.${e.name}`, { defaultValue: e.name });
    const what = e.change?.label ? `: ${e.change.label}` : '';
    lines.push(`${label}${what}${e.ok === false ? ` ${t('quoteCopilot.progress.failed')}` : ''}`);
  }
  const last = events[events.length - 1];
  if (!last || last.type === 'thinking') lines.push(t('quoteCopilot.progress.thinking'));
  return lines;
}

export default function useQuoteCopilotChat({ dealId, dealName = null, quoteId = null, onBeforeTurn, onChanged, enabled = true }) {
  const { t } = useT();
  const { showSuccess, showError } = useToast();
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState([]);
  const [items, setItems] = useState([]);
  const [threadError, setThreadError] = useState(null);
  const [itemsError, setItemsError] = useState(null);
  const [pendingText, setPendingText] = useState(null);
  const [sending, setSending] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const [reverted, setReverted] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);
  const [progress, setProgress] = useState([]);
  const abortRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!dealId || !enabled) return;
    const [th, oi] = await Promise.all([fetchCopilotTurns(dealId), fetchCopilotOpenItems(dealId)]);
    setTurns(th.rows);
    // Only ever SETS the error: the re-read after a refused turn must not wipe the refusal.
    if (th.error) setThreadError(t('quoteCopilot.loadFailed'));
    setItems(oi.rows);
    setItemsError(oi.error);
    setLoaded(true);
  }, [dealId, enabled, t]);

  useEffect(() => { refresh(); }, [refresh]);

  // `text` lets suggestion chips send directly (useChat.send(text) contract).
  const send = useCallback(async (override) => {
    const text = String(typeof override === 'string' ? override : input).trim();
    if (!text || sending || !dealId) return;
    setSending(true);
    setThreadError(null);
    setPendingText(text);
    setInput('');
    const failed = (msg) => { setInput(text); setThreadError(msg || t('quoteCopilot.sendFailed')); }; // keep the question for a re-send
    try {
      // TICKET-899: the host's unsaved edits must be ON the server before a paid turn reads
      // and rewrites the quote -- a failed/offline-queued save stops the turn here.
      const pre = await onBeforeTurn?.();
      if (pre && pre.ok === false) { failed(t('quoteCopilot.saveFirstFailed')); return; }
      // TICKET-912 slice 2b: stream progress into the pending bubble; Stop aborts it.
      const controller = new AbortController();
      abortRef.current = controller;
      setProgress([]);
      const r = await streamCopilotTurn({
        dealId, quoteId, message: text, signal: controller.signal,
        onProgress: (p) => setProgress((prev) => [...prev, p]),
      });
      if (r?.aborted) {
        // The server halts at its next step; anything already written is on the thread
        // (after refresh) with its own Revert. Tell the host the quote may have changed.
        onChanged?.();
      } else if (!r?.success) failed(r?.error);
      else if (r.changes?.wrote) onChanged?.();
    } catch (e) {
      failed(e?.message);
    } finally {
      abortRef.current = null;
      await refresh();
      setProgress([]);
      setPendingText(null);
      setSending(false);
    }
  }, [input, sending, dealId, quoteId, onBeforeTurn, onChanged, refresh, t]);

  const stop = useCallback(() => { abortRef.current?.abort(); }, []);

  const revert = useCallback(async (turnId) => {
    setRevertingId(turnId);
    try {
      const r = await revertCopilotTurn(turnId);
      if (r?.success) {
        setReverted((prev) => new Set(prev).add(turnId));
        showSuccess(t('quoteCopilot.revertDone'));
        onChanged?.();
      } else {
        showError(r?.error || t('quoteCopilot.revertFailed'));
      }
    } catch (e) {
      showError(e?.message || t('quoteCopilot.revertFailed'));
    } finally {
      setRevertingId(null);
    }
  }, [onChanged, showSuccess, showError, t]);

  const copyForEmail = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(t('quoteCopilot.copied'));
    } catch {
      showError(t('quoteCopilot.copyFailed'));
    }
  }, [showSuccess, showError, t]);

  const messages = useMemo(() => {
    const m = turnsToMessages(turns);
    if (pendingText) {
      m.push({ id: 'pending', type: 'user', text: pendingText });
      if (sending) m.push({ id: 'pending-progress', type: 'assistant', text: progressLines(progress, t).join('\n'), progress: true });
    }
    return m;
  }, [turns, pendingText, sending, progress, t]);

  const scope = useMemo(() => (dealId ? [{ type: 'deal', id: dealId, label: dealName || undefined }] : []), [dealId, dealName]);

  return {
    // useChat-compatible surface (AgentShell / ChatComposerV2 / ChatMessageList)
    messages,
    input,
    setInput,
    send,
    stop, // aborts the stream; the server halts the loop at its next step (slice 2b)
    loading: sending,
    hydrating: false,
    error: threadError,
    scope,
    title: dealName,
    sessionId: null,
    setScope: noop,
    addScopeEntry: noop,
    removeScopeEntry: noop,
    clearMessages: noop,
    // copilot extras
    isCopilot: true,
    loaded,
    items,
    itemsError,
    openCount: items.filter((it) => it.status === 'open').length,
    revert,
    reverted,
    revertingId,
    copyForEmail,
    refresh,
  };
}
